interface RetryConfig {
  maxRetries?: number;
  retryDelay?: number;
  onError?: (error: Error, attempt: number) => void;
}

export function useRetryOperation(config: RetryConfig = {}) {
  const {
    maxRetries = 3,
    retryDelay = 1000,
    onError
  } = config;

  const retry = async <T>(operation: () => Promise<T>): Promise<T> => {
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        
        if (onError) {
          onError(lastError, attempt);
        }

        if (attempt === maxRetries) {
          break;
        }

        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }

    throw lastError || new Error('Operation failed after retries');
  };

  return retry;
}

// Example usage:
// const retry = useRetryOperation({
//   maxRetries: 3,
//   retryDelay: 1000,
//   onError: (error, attempt) => {
//     console.log(`Attempt ${attempt} failed:`, error);
//   }
// });
//
// try {
//   const result = await retry(() => fetchData());
// } catch (error) {
//   console.error('All retries failed:', error);
// }
